import { useEffect, useRef } from 'react';
import { Colors, Zindexes } from '../../../theme';
import { useClickOutside, useIntersect } from '../../../hooks';

import styles from './FloatingBox.module.scss';

type Props = {
  /**
   * The horizontal alignment of the box relative to the parent element.
   * The vertical position is handled automatically by state.
   */
  align?: 'right' | 'left' | 'stretch';
  /**
   * What to render in the box.
   */
  children: React.ReactNode;
  /**
   * Changes the background color to white (#fff).
   */
  hasWhiteBackground?: boolean;
  /**
   * Override intersect observer's calculated height for box + children since it isn't always accurate.
   */
  intersectHeight?: number;
  /** Optionally set max width of box. */
  maxWidth?: string;
  /** Optionally set the min width of the box. */
  minWidth?: string;
  /**
   * Optional callback for when a click occurs outside of `FloatingBox`.
   * Typically used to unrender `FloatingBox`.
   */
  onClickOutside?: (event: MouseEvent | TouchEvent) => void;
  /**
   * The vertical alignment of the box relative to the parent element.
   * If `position` is not specified, it is handled automatically by state.
   */
  position?: 'north' | 'south';
  /**
   * Defaults to small (3px).
   */
  borderRadius?: 'small' | 'medium' | 'large' | 'none';

  /**
   * Defaults to `neutral400`.
   * This prop is only intended to be used by the `AccountsMenu` component.
   * See: https://zapier.slack.com/archives/C026EB5GAKZ/p1638885120477000
   */
  borderColor?: string;
};

function getBackgroundColor(hasWhiteBackground = false) {
  return hasWhiteBackground ? '#fff' : Colors.BackgroundWeaker;
}

/**
 * Box that floats and reorients itself if it renders out of the viewport.
 */
export const FloatingBox = ({
  children,
  hasWhiteBackground = undefined,
  intersectHeight = undefined,
  maxWidth = undefined,
  minWidth = undefined,
  onClickOutside = () => {},
  align = 'left',
  position = undefined,
  borderRadius,
  borderColor = Colors.StrokeWeaker,
}: Props) => {
  const threshold = 0.9;
  const node = useRef<HTMLDivElement>(null);
  const [setIntersectNode, { isBottomIntersecting, isTopIntersecting }] =
    useIntersect({
      heightOverride: intersectHeight,
      once: true,
      threshold,
    });

  const backgroundColor = getBackgroundColor(hasWhiteBackground);

  const [setClickOutsideNode] = useClickOutside({
    onClickOutside,
  });

  useEffect(() => {
    setIntersectNode(node.current);
    setClickOutsideNode(node.current);
  }, [setClickOutsideNode, setIntersectNode]);

  const derivedState = {
    position:
      position ||
      (isTopIntersecting || !isBottomIntersecting ? 'south' : 'north'),
  };

  return (
    <div
      className={styles.root}
      data-align={align}
      data-position={derivedState.position}
      ref={node}
      data-testid="floating-box"
      data-zds
      style={{
        '--_zds-floating-box-max-width': maxWidth ?? '',
        '--_zds-floating-box-min-width': minWidth ?? '',
        '--_zds-floating-box-z-index': Zindexes.dropdownMenu,
      }}
    >
      <div
        className={styles.box}
        style={{
          '--_zds-floating-box-background-color': backgroundColor,
          '--_zds-floating-box-border-color': borderColor,
          '--_zds-floating-box-border-radius': `var(--zds-radius-${borderRadius ?? 'default'})`,
        }}
      >
        {children}
      </div>
    </div>
  );
};
